import BaseComponent from 'components/pages/MonthlyBalance';
import {
  getIncomes,
  postIncome,
  patchIncome,
  deleteIncome,
} from 'domains/api/incomes';
import {
  getOutgos,
  postOutgo,
  patchOutgo,
  deleteOutgo,
} from 'domains/api/outgos';
import { searchMonthlyTotalAsset } from 'domains/api/monthly_total_assets';
import { resetCurrentUser } from 'domains/current_user';
import { nextYearMonth, previousYearMonth } from 'libs/year_month';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Income, MonthlyTotalAsset, Outgo } from 'types';

const MonthlyBalance: React.FC = () => {
  const { year, month } = useParams();

  const defaultYear = new Date().getFullYear();
  const defaultMonth = new Date().getMonth() + 1;
  const currentYear = year ? parseInt(year, 10) : defaultYear;
  const currentMonth = month ? parseInt(month, 10) : defaultMonth;

  const navigate = useNavigate();
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [isLoadingIncomes, setIsLoadingIncomes] = useState(false);
  useEffect(() => {
    setIsLoadingIncomes(true);
    void getIncomes(currentYear, currentMonth)
      .then((response) => {
        setIncomes(response.incomes);
        setIsLoadingIncomes(false);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  }, [navigate, currentYear, currentMonth]);

  const [outgos, setOutgos] = useState<Outgo[]>([]);
  const [isLoadingOutgos, setIsLoadingOutgos] = useState(false);
  useEffect(() => {
    setIsLoadingOutgos(true);
    void getOutgos(currentYear, currentMonth)
      .then((response) => {
        setOutgos(response.outgos);
        setIsLoadingOutgos(false);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  }, [navigate, currentYear, currentMonth]);

  const [currentTotalAsset, setCurrentTotalAsset] =
    useState<MonthlyTotalAsset | null>(null);
  const [isLoadingCurrentAsset, setIsLoadingCurrentAsset] = useState(false);
  useEffect(() => {
    setIsLoadingCurrentAsset(true);
    void searchMonthlyTotalAsset(currentYear, currentMonth)
      .then((response) => {
        setCurrentTotalAsset(response.monthly_total_asset);
        setIsLoadingCurrentAsset(false);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  }, [navigate, currentYear, currentMonth]);

  const [previousTotalAsset, setPreviousTotalAsset] =
    useState<MonthlyTotalAsset | null>(null);
  const [isLoadingPreviousTotalAsset, setIsLoadingPreviousAsset] =
    useState(false);
  useEffect(() => {
    setIsLoadingPreviousAsset(true);
    const preYearMonth = previousYearMonth(currentYear, currentMonth);
    void searchMonthlyTotalAsset(preYearMonth.year, preYearMonth.month)
      .then((response) => {
        setPreviousTotalAsset(response.monthly_total_asset);
        setIsLoadingPreviousAsset(false);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  }, [navigate, currentYear, currentMonth]);

  const onClickNextMonth = () => {
    const next = nextYearMonth(currentYear, currentMonth);
    navigate(`/monthly_balances/${next.year}/${next.month}`);
  };

  const onClickPreviousMonth = () => {
    const previous = previousYearMonth(currentYear, currentMonth);
    navigate(`/monthly_balances/${previous.year}/${previous.month}`);
  };

  const onClickImportedOutgo = () => {
    navigate('/imported_outgos');
  };

  const onSubmitCreateOutgo = (outgo: Outgo) => {
    void postOutgo(outgo)
      .then((_) => {
        navigate(0);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  };

  const onSubmitUpdateOutgo = (outgo: Outgo) => {
    void patchOutgo(outgo)
      .then((_) => {
        navigate(0);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  };

  const onSubmitCreateIncome = (income: Income) => {
    void postIncome(income)
      .then((_) => {
        navigate(0);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  };

  const onSubmitUpdateIncome = (income: Income) => {
    void patchIncome(income)
      .then((_) => {
        navigate(0);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  };

  const onClickDeleteOutgo = (outgo: Outgo) => {
    void deleteOutgo(outgo)
      .then((_) => {
        navigate(0);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  };

  const onClickDeleteIncome = (income: Income) => {
    void deleteIncome(income)
      .then((_) => {
        navigate(0);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  };

  return (
    <BaseComponent
      year={currentYear}
      month={currentMonth}
      incomes={incomes}
      outgos={outgos}
      previousMonthTotalAsset={
        (previousTotalAsset?.bank || 0) + (previousTotalAsset?.wallet || 0)
      }
      currentMonthTotalAsset={
        (currentTotalAsset?.bank || 0) + (currentTotalAsset?.wallet || 0)
      }
      isLoading={
        isLoadingIncomes &&
        isLoadingOutgos &&
        isLoadingCurrentAsset &&
        isLoadingPreviousTotalAsset
      }
      onClickPreviousMonth={onClickPreviousMonth}
      onClickNextMonth={onClickNextMonth}
      onClickImportedOutgo={onClickImportedOutgo}
      onClickDeleteOutgo={onClickDeleteOutgo}
      onClickDeleteIncome={onClickDeleteIncome}
      onSubmitClickCreateOutgo={onSubmitCreateOutgo}
      onSubmitClickUpdateOutgo={onSubmitUpdateOutgo}
      onSubmitClickCreateIncome={onSubmitCreateIncome}
      onSubmitClickUpdateIncome={onSubmitUpdateIncome}
    />
  );
};

export default MonthlyBalance;
